import React, { useState, useEffect, useMemo } from "react"
import {
  Typography,
  Space,
  Button,
  Tabs,
  Card,
  Tag,
  Tooltip,
  Badge,
  Divider,
  Skeleton,
  Row,
  Col,
  Empty,
  Layout,
  Avatar,
  message
} from "antd"
import {
  StarOutlined,
  ShareAltOutlined,
  DownloadOutlined,
  MailOutlined,
  PhoneOutlined,
  LeftOutlined,
  RightOutlined,
  MoreOutlined,
  UserOutlined,
  FileTextOutlined,
  HistoryOutlined,
  PrinterOutlined,
  CopyOutlined,
  EnvironmentOutlined,
  CalendarOutlined,
  LinkedinOutlined,
  DatabaseOutlined,
  EyeOutlined,
  EditOutlined,
  BankOutlined,
  BookOutlined
} from "@ant-design/icons"
import DocViewer, { DocViewerRenderers } from "@cyntler/react-doc-viewer"
import moment from "moment"
import { useCandidateDetails } from "../hooks/useCandidateDetails"
import Notes from "../../../Modals/Notes"
import { useCallAPI } from "../../../../../hooks/useCallAPI"
import { CANDIDATE_NOTE_UPDATE, CANDIDATE_NOTE_DELETE, CANDIDATE_UPDATE_PARTIALLY, CANDIDATE_CV_UPLOAD } from "../../../../../ApiUrl"
import { useSelector } from "react-redux"
import cssStyles from './Monster.module.css'
import { useNotes } from '../context/notesContext';
import axios from "axios"
import { requestHeader } from "../../../../../others/header"

const { Text, Title, Paragraph } = Typography
const { TabPane } = Tabs
const { Content } = Layout


const styles = {
  container: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    backgroundColor: "#f5f7fa",
  },
  header: {
    padding: "12px 24px",
    borderBottom: "1px solid #f0f0f0",
    backgroundColor: "#fff",
    zIndex: 1,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center"
  },
  content: {
    flex: 1,
    overflow: "auto",
    padding: "24px",
    backgroundColor: "#f5f7fa"
  },
  section: {
    marginBottom: "24px",
  },
  navigationButton: {
    color: "#1890ff",
    border: "none",
    background: "transparent",
    fontSize: "16px"
  },
  matchScore: {
    marginRight: 16,
    backgroundColor: "#e6f7ff",
    border: "1px solid #91d5ff",
    color: "#1890ff",
    padding: "2px 10px",
    borderRadius: "12px",
    fontSize: "12px",
    fontWeight: "bold",
  },
  avatar: {
    width: 120,
    height: 120,
    marginBottom: 16,
    backgroundColor: '#e6f7ff',
    color: '#1890ff',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    fontSize: '48px',
    borderRadius: '50%'
  },
  sectionTitle: {
    marginBottom: '12px',
    borderBottom: '1px solid #f0f0f0',
    paddingBottom: '8px',
    color: '#1890ff'
  },
  card: {
    borderRadius: '8px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
    marginBottom: '16px'
  },
  workHistoryCard: {
    marginBottom: '16px',
    borderLeft: '3px solid #1890ff',
    boxShadow: '0 2px 8px rgba(0,0,0,0.09)'
  },
  educationCard: {
    marginBottom: '16px',
    borderLeft: '3px solid #1890ff',
    boxShadow: '0 2px 8px rgba(0,0,0,0.09)'
  },
  summaryCard: {
    backgroundColor: '#f9f9f9',
    borderRadius: '8px',
    borderLeft: '4px solid #1890ff',
    maxHeight: '300px',
    overflow: 'auto'
  },
  iconStyle: {
    color: '#1890ff',
    marginRight: '8px',
    fontSize: '16px'
  },
  bulletPoint: {
    color: '#1890ff',
    marginRight: '8px',
    fontSize: '16px',
    marginTop: '2px'
  },
  tabsStyle: {
    backgroundColor: '#fff',
    padding: '16px',
    borderRadius: '8px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.09)'
  },
  viewedTag: {
    backgroundColor: '#e6f7ff',
    borderColor: '#91d5ff',
    color: '#1890ff',
    marginLeft: '8px',
    fontSize: '12px'
  },
  headerControls: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  basicInfoContainer: {
    padding: '24px',
    backgroundColor: '#fff',
    borderRadius: '8px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
    marginBottom: '24px'
  },
  contactInfoItem: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '8px'
  },
  candidateName: {
    fontSize: '24px',
    marginBottom: '4px',
    fontWeight: 'bold',
    color: '#333'
  },
  candidateTitle: {
    color: '#555',
    marginBottom: '16px',
    fontSize: '16px'
  },
  tabsContainer: {
    marginTop: '16px',
    borderBottom: '1px solid #f0f0f0'
  }
}


const ProfileSection = ({ title, children }) => (
  <div style={{ marginBottom: '24px' }}>
    <Title level={5} style={styles.sectionTitle}>
      {title}
    </Title>
    <div style={{ paddingLeft: '8px' }}>{children}</div>
  </div>
);


const WorkHistoryItem = ({ company, title, tenure, description }) => (
  <Card
    size="small"
    style={styles.workHistoryCard}
  >
    <div style={{ padding: '8px' }}>
      <Text strong style={{ fontSize: '16px', display: 'block' }}>{title}</Text>
      <Text style={{ display: 'block', color: '#555' }}>{company}</Text>
      <Text type="secondary">{tenure}</Text>
      {description && <Paragraph style={{ marginTop: '8px' }}>{description}</Paragraph>}
    </div>
  </Card>
);


const EducationItem = ({ school, major, degree, graduationDate }) => (
  <Card
    size="small"
    style={styles.educationCard}
  >
    <div style={{ padding: '8px' }}>
      <Text strong style={{ fontSize: '16px', display: 'block' }}>{school}</Text>
      {degree && <Text style={{ display: 'block', color: '#555' }}>{degree} {major ? `in ${major}` : ''}</Text>}
      {graduationDate && <Text type="secondary">{graduationDate}</Text>}
    </div>
  </Card>
);

const CandidateActions = ({ onAction, isInDb, isCompared }) => {
  const actionItems = [
    // {
    //   key: "addToDb",
    //   icon: <DatabaseOutlined />,
    //   label: isInDb ? "Already in Database" : "Add to Database",
    //   disabled: isInDb
    // },
    // {
    //   key: "compare",
    //   icon: <CopyOutlined />,
    //   label: isCompared ? "Remove from Compare" : "Add to Compare"
    // }
  ]

  return (
    <Space>
      {actionItems.map(({ key, icon, label, disabled }) => (
        <Tooltip key={key} title={label}>
          <Button
            icon={icon}
            onClick={() => onAction(key)}
            type={(key === "addToDb" && isInDb) || (key === "compare" && isCompared) ? "primary" : "default"}
            style={(key === "compare" && isCompared) ? { backgroundColor: '#1890ff', borderColor: '#1890ff', color: '#fff' } : {}}
            disabled={disabled}
          />
        </Tooltip>
      ))}
    </Space>
  )
}

const NavigationButtons = ({ onPrevious, onNext, hasPrevious, hasNext }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
    <Button
      type="text"
      icon={<LeftOutlined />}
      onClick={onPrevious}
      disabled={!hasPrevious}
      style={styles.navigationButton}
    />
    <Button
      type="text"
      icon={<RightOutlined />}
      onClick={onNext}
      disabled={!hasNext}
      style={styles.navigationButton}
    />
  </div>
)

const CandidateDetailView = ({
  candidate,
  onNext,
  onPrevious,
  hasNext,
  hasPrevious,
  onAction,
  isInDb,
  isCompared,
  onBack
}) => {
  const { fetchDetails, detailsCache, loading, error } = useCandidateDetails()
  const [activeTab, setActiveTab] = useState("profile")
  const [showNotes, setShowNotes] = useState(false)
  const [selectedRow, setSelectedRow] = useState(null)
  const [candidateDbId, setCandidateDbId] = useState(null)
  const [notesData, setNotesData] = useState({ data: { data: { multipleNotes: [] } }, success: true })


  const { syncNotes, addNote, updateNote, deleteNote, getCandidateNotes, getLastUpdated, syncCandidateDbId } = useNotes();

  const updateCandidateNote = async (value, id) => {
    try {
      const response = await axios.patch(`${CANDIDATE_NOTE_UPDATE}/${candidateDbId}/${id}`, value, requestHeader());

      return response.data;
    } catch (error) {
      console.error("Error updating note:", error);
      throw error;
    }
  };

  const deleteCandidateNote = async (id) => {
    try {
      const response = await axios.delete(`${CANDIDATE_NOTE_DELETE}/${candidateDbId}/${id}`, requestHeader());

      return response.data;
    } catch (error) {
      console.error("Error deleting note:", error);
      throw error;
    }
  };

  const updateCandidatePartially = async (data, id = candidateDbId) => {
    try {
      const response = await axios.patch(`${CANDIDATE_UPDATE_PARTIALLY}/${id}`, data, requestHeader());
      return response.data;
    } catch (error) {
      console.error("Error updating candidate:", error);
      throw error;
    }
  };

  const users = useSelector(({ users }) => users)

  const sid = candidate?.id
  const currentError = sid ? error[sid] : null

  const isLoading = loading[sid] || false

  const currentDetails = sid ? detailsCache[sid]?.data : null

  const [isViewed, setIsViewed] = useState(candidate?.isViewed || false);

  useEffect(() => {
    if (sid && detailsCache[sid]) {

      const candidateId = detailsCache[sid].candidateId ||
        (detailsCache[sid].data && detailsCache[sid].data.candidateId) ||
        (detailsCache[sid].data && detailsCache[sid].data._id) ||
        null;


      setCandidateDbId(candidateId);
      syncCandidateDbId(sid, candidateDbId);


      if (detailsCache[sid].data) {
        let notes = null;


        if (detailsCache[sid].data.notes && Array.isArray(detailsCache[sid].data.notes)) {
          notes = detailsCache[sid].data.notes;
        } else if (detailsCache[sid].data.multipleNotes && Array.isArray(detailsCache[sid].data.multipleNotes)) {
          notes = detailsCache[sid].data.multipleNotes;
        }

        if (notes && Array.isArray(notes)) {

          setNotesData({
            data: {
              data: {
                multipleNotes: notes
              }
            },
            success: true
          });

          setTimeout(() => {
            syncNotes(sid, notes);
          }, 100);
        }
      }


      setSelectedRow({
        _id: candidateId,
        ...currentDetails
      });
    }

  }, [sid, detailsCache, currentDetails]);


  const [lastSyncTime, setLastSyncTime] = useState(0);

  useEffect(() => {
    if (!sid) return;

    const lastUpdateTime = getLastUpdated(sid);

    if (lastUpdateTime > lastSyncTime) {
      const contextNotes = getCandidateNotes(sid);

      setNotesData({
        data: {
          data: {
            multipleNotes: contextNotes
          }
        },
        success: true
      });


      if (contextNotes.length === 0) {
        setShowNotes(false);
      }

      setLastSyncTime(Date.now());
    }
  }, [sid, getCandidateNotes, getLastUpdated, lastSyncTime]);

  useEffect(() => {
    if (sid) {
      fetchDetails(candidate)
        .then(details => {
          if (details?._id) {
            setCandidateDbId(details._id);
            syncCandidateDbId(sid, details._id);
          }
        })
        .catch((err) => console.error("Error fetching candidate details:", err))
    }
  }, [sid, fetchDetails, candidate])

  useEffect(() => {
    if (activeTab === "notes" && candidateDbId) {
      setShowNotes(true);
    }
  }, [activeTab, candidateDbId])


  useEffect(() => {
    if (sid && !isViewed) {

      setIsViewed(true);

      if (candidateDbId) {
        console.log("Marking candidate as viewed:", candidateDbId);
        updateCandidatePartially({
          id: candidateDbId,
          isViewed: true,
          viewHistory: [...(candidate.viewHistory || []), {
            viewedAt: new Date().toISOString(),
            viewedBy: users.user?.id || 'unknown'
          }]
        })
          .then(() => {
            console.log("Successfully marked candidate as viewed");
          })
          .catch(err => {
            console.error("Error marking candidate as viewed:", err);
          });
      }
    }
  }, [sid, candidateDbId, isViewed, candidate.viewHistory, users.user]);

  const handleAction = (action) => {
    onAction(action, candidate.id);
  }


  const getNotes = () => {
    if (candidateDbId) {


      const contextNotes = getCandidateNotes(sid);
      if (contextNotes.length > 0) {
        setNotesData({
          data: {
            data: {
              multipleNotes: contextNotes
            }
          },
          success: true
        });
      }
      else if (currentDetails?.multipleNotes) {
        setNotesData({
          data: {
            data: {
              multipleNotes: currentDetails.multipleNotes
            }
          },
          success: true
        });

        syncNotes(sid, currentDetails.multipleNotes);
      }

      setSelectedRow({
        _id: candidateDbId,
        ...currentDetails
      });
      setShowNotes(true);
    } else {
      console.warn("[Monster CandidateDetailView] Cannot access notes: No database candidate ID available");
      message.warning("Cannot access notes: Candidate not found in database");
    }
  };

  const handleNotes = async (value) => {
    if (candidateDbId) {
      try {
        const noteWithUser = {
          ...value,
          created_by: {
            _id: users.user?._id || 'unknown',
            display_name: users.user?.display_name || 'Unknown User'
          },
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          updated_by: {
            _id: users.user?._id || 'unknown',
            display_name: users.user?.display_name || 'Unknown User'
          }
        };

        const response = await updateCandidatePartially({
          multipleNotes: noteWithUser
        });

        const newNoteWithId = {
          ...noteWithUser,
          _id: response?.data?.multipleNotes?.[0]?._id
        };


        const currentNotes = notesData?.data?.data?.multipleNotes || [];
        const updatedNotes = [...currentNotes, newNoteWithId];

        setNotesData({
          data: {
            data: {
              multipleNotes: updatedNotes
            }
          },
          success: true
        });

        setTimeout(() => {
          syncNotes(sid, updatedNotes);
        }, 100);

        if (currentDetails) {
          currentDetails.multipleNotes = updatedNotes;
        }

        message.success("Note added successfully");

      } catch (err) {
        console.error("Error adding note:", err);
        message.error("Failed to add note");
      }
    } else {
      message.warning("Cannot add notes: Candidate not found in database");
    }
  };


  useEffect(() => {
    if (sid) {
      const contextNotes = getCandidateNotes(sid)
    }
  }, [sid, getCandidateNotes, notesData]);

  const handleUpdateNotes = (value, id) => {
    if (candidateDbId) {

      const noteUpdate = {
        ...value,
        updated_at: new Date().toISOString(),
        updated_by: {
          _id: users.user?._id || 'unknown',
          display_name: users.user?.display_name || 'Unknown User'
        }
      };

      updateCandidateNote(noteUpdate, id)
        .then(response => {
          message.success("Notes updated successfully");

          const currentNotes = notesData?.data?.data?.multipleNotes || [];
          const updatedNotes = currentNotes.map(note =>
            note._id === id ? { ...note, ...noteUpdate } : note
          );

          setNotesData({
            data: {
              data: {
                multipleNotes: updatedNotes
              }
            },
            success: true
          });

          setTimeout(() => {
            syncNotes(sid, updatedNotes);
          }, 100);

          if (currentDetails) {
            currentDetails.multipleNotes = updatedNotes;
          }
        })
        .catch(err => {
          console.error("Error updating notes:", err);
          message.error("Failed to update notes");
        });
    } else {
      message.warning("Cannot update notes: Candidate not found in database");
    }
  };

  const handleDeleteNotes = (id) => {
    if (candidateDbId) {
      deleteCandidateNote(id)
        .then(response => {
          message.success("Notes deleted successfully");

          const updatedNotes = response?.data?.multipleNotes ||
            response?.data?.data?.multipleNotes ||
            currentDetails.multipleNotes.filter(note => note._id !== id);

          deleteNote(sid, id);

          setNotesData({
            data: {
              data: {
                multipleNotes: updatedNotes
              }
            },
            success: true
          });

          setTimeout(() => {
            syncNotes(sid, updatedNotes);
          }, 150);

          if (currentDetails) {
            currentDetails.multipleNotes = updatedNotes;
          }
        })
        .catch(err => {
          console.error("Error deleting notes:", err);
          message.error("Failed to delete notes");
        });
    } else {
      message.warning("Cannot delete notes: Candidate not found in database");
    }
  };

  const renderLocation = (locationDetails) => {
    if (!locationDetails) return candidate.location;

    if (typeof locationDetails === 'object') {
      const parts = [];
      if (locationDetails.city) parts.push(locationDetails.city);
      if (locationDetails.state) parts.push(locationDetails.state);
      if (locationDetails.country) parts.push(locationDetails.country);
      if (locationDetails.zipCode) parts.push(locationDetails.zipCode);
      if (locationDetails.cityName) parts.push(locationDetails.cityName);
      if (locationDetails.stateName) parts.push(locationDetails.stateName);
      if (locationDetails.countryName) parts.push(locationDetails.countryName);
      if (locationDetails.postalCode) parts.push(locationDetails.postalCode);

      return parts.join(', ');
    }

    return candidate.location || currentDetails?.current_location || currentDetails?.home_town;
  };

  const renderHeader = () => (
    <div style={styles.header}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <Button
          type="text"
          icon={<LeftOutlined />}
          onClick={onBack}
          style={{ color: '#1890ff', marginRight: '8px' }}
        >
          Back to Results
        </Button>
        <Title level={4} style={{ margin: 0 }}>
          {currentDetails?.candidateDetails?.personalInfo
            ? `${currentDetails.candidateDetails.personalInfo.firstName} ${currentDetails.candidateDetails.personalInfo.lastName}`
            : currentDetails?.first_name
              ? `${currentDetails.first_name} ${currentDetails.last_name}`
              : candidate.name || "Confidential Candidate"}
        </Title>
        {isViewed && (
          <Tag icon={<EyeOutlined />} style={styles.viewedTag}>
            Viewed
          </Tag>
        )}
      </div>
      <div style={styles.headerControls}>
        <NavigationButtons onPrevious={onPrevious} onNext={onNext} hasPrevious={hasPrevious} hasNext={hasNext} />

        <CandidateActions onAction={handleAction} isInDb={isInDb} isCompared={isCompared} />
      </div>
    </div>
  );

  const renderBasicInfo = () => {
    const name = currentDetails?.first_name
      ? `${currentDetails.first_name} ${currentDetails.last_name}`
      : currentDetails?.candidateDetails?.personalInfo
        ? `${currentDetails.candidateDetails.personalInfo.firstName} ${currentDetails.candidateDetails.personalInfo.lastName}`
        : candidate.name;

    const jobTitle = currentDetails?.desired_job_type ||
      currentDetails?.candidateDetails?.desiredJob?.title ||
      candidate.currentPosition?.title;

    const email = currentDetails?.email ||
      currentDetails?.candidateDetails?.personalInfo?.email;
    const phone = currentDetails?.mobile ||
      currentDetails?.candidateDetails?.personalInfo?.phone;
    const location = renderLocation(
      currentDetails?.candidateDetails?.personalInfo?.location ||
      candidate.locationDetails
    );

    return (
      <div style={styles.basicInfoContainer}>
        <Row gutter={24} align="top">
          <Col xs={24} sm={6} style={{ textAlign: 'center' }}>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <div style={styles.avatar}>
                <UserOutlined style={{ fontSize: '60px' }} />
              </div>
            </div>
          </Col>
          <Col xs={24} sm={18}>
            <div>
              <div style={styles.candidateName}>
                {name || "Confidential Candidate"}
              </div>
              <div style={styles.candidateTitle}>
                {jobTitle || "Not specified"}
              </div>

              <Space direction="vertical" size={12} style={{ width: '100%' }}>
                <div style={styles.contactInfoItem}>
                  <EnvironmentOutlined style={styles.iconStyle} />
                  <span>{location}</span>
                </div>

                {phone && (
                  <div style={styles.contactInfoItem}>
                    <PhoneOutlined style={styles.iconStyle} />
                    <span>{phone}</span>
                  </div>
                )}

                {email && (
                  <div style={styles.contactInfoItem}>
                    <MailOutlined style={styles.iconStyle} />
                    <span>{email}</span>
                  </div>
                )}

                <div style={styles.contactInfoItem}>
                  <CalendarOutlined style={styles.iconStyle} />
                  <span>Updated {moment(candidate.dateModified || new Date()).format("LL")}</span>
                </div>
              </Space>
            </div>
          </Col>
        </Row>
      </div>
    );
  };

  const renderAdditionalInfo = () => {
    if (!currentDetails) {
      return null;
    }

    const infoFields = [
      (currentDetails.total_work_exp_year || currentDetails.candidateDetails?.experience?.years) && {
        label: "Total Experience",
        value: currentDetails.candidateDetails?.experience?.years ||
          `${currentDetails.total_work_exp_year} years ${currentDetails.total_work_exp_month ? `${currentDetails.total_work_exp_month} months` : ''}`,
        icon: <HistoryOutlined style={styles.iconStyle} />
      },
      (candidate.location || currentDetails.current_location || currentDetails.candidateDetails?.personalInfo?.location) && {
        label: "Current Location",
        value: renderLocation(
          currentDetails.candidateDetails?.personalInfo?.location ||
          candidate.locationDetails
        ),
        icon: <EnvironmentOutlined style={styles.iconStyle} />
      },
      currentDetails.home_town && {
        label: "Home Town",
        value: currentDetails.home_town,
        icon: <EnvironmentOutlined style={styles.iconStyle} />
      },
      (currentDetails.area_pin_code || currentDetails.candidateDetails?.personalInfo?.zipCode) && {
        label: "Area Pin Code",
        value: currentDetails.area_pin_code || currentDetails.candidateDetails?.personalInfo?.zipCode,
        icon: <EnvironmentOutlined style={styles.iconStyle} />
      },
      currentDetails.profile_strength && {
        label: "Profile Strength",
        value: `${currentDetails.profile_strength}%`,
        icon: <UserOutlined style={styles.iconStyle} />
      },
      currentDetails.last_active && {
        label: "Last Active",
        value: moment(currentDetails.last_active).format("LL"),
        icon: <CalendarOutlined style={styles.iconStyle} />
      }
    ].filter(Boolean);

    if (infoFields.length === 0) {
      return null;
    }

    return (
      <ProfileSection title="Additional Information">
        <Card style={{ borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
          <Row gutter={[16, 16]}>
            {infoFields.map((field, index) => (
              <Col xs={24} sm={12} key={index}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  {field.icon || <CalendarOutlined style={styles.iconStyle} />}
                  <div>
                    <Text type="secondary" style={{ display: 'block', fontSize: '12px' }}>{field.label}</Text>
                    <Text strong>{field.value}</Text>
                  </div>
                </div>
              </Col>
            ))}
          </Row>
        </Card>
      </ProfileSection>
    );
  };

  const renderExperience = () => {
    let experienceItems = [];

    if (currentDetails?.candidateDetails?.experience?.positions?.length > 0) {
      experienceItems = currentDetails.candidateDetails.experience.positions.map((job) => ({
        title: job.title || 'No Title',
        company: job.organization || 'No Organization',
        tenure: job.startDate?.year ?
          `${job.startDate.month}/${job.startDate.year} - ${job.isCurrent ? "Present" :
            (job.endDate?.year) ?
              `${job.endDate.month}/${job.endDate.year}` :
              "End date not specified"
          }` : 'Dates not specified',
        description: job.description || null
      }));
    } else if (currentDetails?.employees?.length > 0) {
      experienceItems = currentDetails.employees.map((job) => ({
        title: job.designation || 'No Title',
        company: job.organization || 'No Organization',
        tenure: job.work_since_from_month && job.work_since_from_year ?
          `${job.work_since_from_month}/${job.work_since_from_year} - ${job.is_current_company ? "Present" :
            (job.work_since_to_month && job.work_since_to_year) ?
              `${job.work_since_to_month}/${job.work_since_to_year}` :
              "End date not specified"
          }` : 'Dates not specified',
        description: job.description
      }));
    } else if (candidate.currentPosition) {
      experienceItems = [{
        title: candidate.currentPosition.title,
        company: candidate.currentPosition.company,
        tenure: 'Current position'
      }];
    }

    return (
      <ProfileSection title="Experience">
        {experienceItems.length > 0 ? (
          experienceItems.map((job, index) => (
            <WorkHistoryItem
              key={index}
              company={job.company}
              title={job.title}
              tenure={job.tenure}
              description={job.description}
            />
          ))
        ) : (
          <Empty description="No experience information available" />
        )}
      </ProfileSection>
    );
  };

  const renderEducation = () => {
    let educationItems = [];

    if (currentDetails?.candidateDetails?.education) {
      const eduData = currentDetails.candidateDetails.education;
      if (eduData && eduData._) {
        educationItems = [{
          school: "Not specified",
          degree: eduData._ || "Not specified",
          major: "",
          startYear: "",
          endYear: ""
        }];
      }
    } else if (currentDetails?.candidate_qualifications?.length > 0) {
      educationItems = currentDetails.candidate_qualifications.map((edu) => ({
        school: edu.institution || 'Not specified',
        degree: edu.degree || 'Not specified',
        major: edu.field_of_study,
        startYear: edu.start_year,
        endYear: edu.end_year
      }));
    }

    return (
      <ProfileSection title="Education">
        {educationItems.length > 0 ? (
          educationItems.map((edu, index) => (
            <EducationItem
              key={index}
              school={edu.school}
              degree={edu.degree}
              major={edu.major}
              graduationDate={
                edu.startYear
                  ? (edu.startYear + (edu.endYear ? ' - ' + edu.endYear : ''))
                  : (edu.endYear ? edu.endYear : 'Present')
              }
            />
          ))
        ) : (
          <Empty description="No education information available" />
        )}
      </ProfileSection>
    );
  };

  const renderSkills = () => {
    let skillItems = [];

    if (currentDetails?.candidateDetails?.skills?.length > 0) {

      skillItems = currentDetails.candidateDetails.skills.map(skill =>
        skill.name || ''
      ).filter(skill => skill !== '');
    } else if (currentDetails?.candidate_it_skills?.length > 0) {

      skillItems = currentDetails.candidate_it_skills.map(skill =>
        skill.skill_name || ''
      ).filter(skill => skill !== '');
    }

    return (
      <ProfileSection title="Skills">
        <Card style={{ borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {skillItems.length > 0 ? (
              skillItems.map((skill, index) => {

                let tagColor = 'blue';
                const skillText = typeof skill === 'string' ? skill.toLowerCase() : '';

                if (skillText.includes('java') || skillText.includes('spring') || skillText.includes('hibernate')) {
                  tagColor = 'cyan';
                } else if (skillText.includes('react') || skillText.includes('angular') || skillText.includes('vue') ||
                  skillText.includes('javascript') || skillText.includes('node') || skillText.includes('html') ||
                  skillText.includes('css')) {
                  tagColor = 'blue';
                } else if (skillText.includes('aws') || skillText.includes('cloud') || skillText.includes('docker') ||
                  skillText.includes('kubernetes') || skillText.includes('devops')) {
                  tagColor = 'volcano';
                } else if (skillText.includes('sql') || skillText.includes('database') || skillText.includes('oracle') ||
                  skillText.includes('mysql') || skillText.includes('mongo')) {
                  tagColor = 'purple';
                } else if (skillText.includes('agile') || skillText.includes('scrum') || skillText.includes('jira') ||
                  skillText.includes('management')) {
                  tagColor = 'orange';
                }

                return (
                  <Tag
                    key={index}
                    color={tagColor}
                    style={{
                      padding: '6px 10px',
                      borderRadius: '16px',
                      fontSize: '13px',
                      margin: '4px'
                    }}
                  >
                    {skill}
                  </Tag>
                );
              })
            ) : (
              <Empty description="No skills information available" />
            )}
          </div>
        </Card>
      </ProfileSection>
    );
  };

  const renderResume = () => {
    const hasAttachment = currentDetails?.attachments ||
      (currentDetails?.candidateDetails?.profile?.hasResumeDoc === 1);

    if (!hasAttachment) {
      return <Empty description="No resume available" />;
    }

    const attachmentPath = currentDetails?.attachments ||
      `${currentDetails?.textResumeID}.pdf`;
      
    const fileType = attachmentPath.split('.').pop().toLowerCase();
    
    // Use the same CANDIDATE_CV_UPLOAD base path as in CandidateProfileDetail
    const resumeUrl = `${CANDIDATE_CV_UPLOAD}${attachmentPath}`;

    const document = {
      uri: resumeUrl,
      fileType: fileType
    };

    const handleDownload = () => {
      window.open(resumeUrl, '_blank');
    };

    return (
      <Card
        style={{ margin: "0 auto", width: "100%" }}
        extra={
          <Button
            icon={<DownloadOutlined />}
            type="primary"
            onClick={handleDownload}
          >
            Download
          </Button>
        }
      >
        <div style={{ height: "calc(100vh - 250px)" }}>
          <DocViewer
            documents={[document]}
            pluginRenderers={DocViewerRenderers}
            style={{ height: "100%", width: "100%" }}
            config={{
              header: {
                disableHeader: true,
                disableFileName: true,
                retainURLParams: false
              }
            }}
          />
        </div>
      </Card>
    );
  };

  const renderNotes = () => {
    if (!candidateDbId) {
      return (
        <Card title="Notes" style={styles.section}>
          <Empty
            description={
              <span>
                {isLoading
                  ? "Loading candidate details..."
                  : "Candidate not found in database. Notes cannot be accessed."}
              </span>
            }
          />
        </Card>
      )
    }

    return (
      <Card title="Notes" style={styles.section}>
        <Notes
          handleCancel={() => { }}
          isVisible={true}
          handleNotes={handleNotes}
          notes={notesData}
          getNotes={() => { }}
          jobOpenings={{ _id: candidateDbId, ...selectedRow }}
          user={users}
          updateNoteState={() => { }}
          handleUpdateNote={handleUpdateNotes}
          handleDeleteNote={handleDeleteNotes}
          notesField={"multipleNotes"}
          isModal={false}
        />
      </Card>
    )
  }

  if (currentError) {
    return <Empty description={`Error: ${currentError}`} />
  }


  const notesCount = useMemo(() => {

    const contextNotes = getCandidateNotes(sid);
    if (contextNotes && contextNotes.length > 0) {
      return contextNotes.length;
    }


    if (notesData?.data?.data?.multipleNotes) {
      return notesData.data.data.multipleNotes.length;
    }


    if (currentDetails?.multipleNotes && Array.isArray(currentDetails.multipleNotes)) {
      return currentDetails.multipleNotes.length;
    }

    if (currentDetails?.notes && Array.isArray(currentDetails.notes)) {
      return currentDetails.notes.length;
    }

    return 0;
  }, [sid, getCandidateNotes, notesData, currentDetails]);


  const renderTabs = () => (
    <div style={styles.tabsContainer}>
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        tabBarStyle={{
          color: '#1890ff',
          fontWeight: 'normal',
          marginBottom: 0
        }}
        tabBarGutter={48}
      >
        <TabPane
          tab={
            <span style={{ display: 'flex', alignItems: 'center' }}>
              <UserOutlined style={{ marginRight: '8px' }} />
              Profile
            </span>
          }
          key="profile"
        />
        <TabPane
          tab={
            <span style={{ display: 'flex', alignItems: 'center' }}>
              <FileTextOutlined style={{ marginRight: '8px' }} />
              Resume
            </span>
          }
          key="resume"
        />
        <TabPane
          tab={
            <span style={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
              <EditOutlined style={{ marginRight: '8px' }} />
              Notes
              {notesCount > 0 && (
                <Badge
                  count={notesCount}
                  size="small"
                  style={{
                    backgroundColor: '#1890ff',
                    marginLeft: '8px',
                    boxShadow: 'none'
                  }}
                />
              )}
            </span>
          }
          key="notes"
        />
      </Tabs>
    </div>
  );

  return (
    <Layout style={styles.container} className={cssStyles.monsterContainer}>
      {renderHeader()}

      <Content style={styles.content}>
        {renderTabs()}

        <div style={{ marginTop: '24px' }}>
          {activeTab === "profile" && (
            <>
              {renderBasicInfo()}
              {isLoading ? (
                <Skeleton active paragraph={{ rows: 10 }} />
              ) : (
                <>
                  {/* Profile Summary Section - if available */}
                  {currentDetails?.profile_summary && (
                    <>
                      <ProfileSection title="Professional Summary">
                        <Card style={styles.summaryCard}>
                          <div style={{ padding: '12px' }}>
                            <Paragraph style={{
                              lineHeight: '1.6',
                              fontSize: '14px',
                              color: '#333'
                            }}>
                              {currentDetails.profile_summary}
                            </Paragraph>
                          </div>
                        </Card>
                      </ProfileSection>
                      <Divider style={{ margin: '16px 0' }} />
                    </>
                  )}

                  {renderAdditionalInfo()}
                  {renderExperience()}
                  {renderEducation()}
                  {renderSkills()}
                </>
              )}
            </>
          )}

          {activeTab === "resume" && (
            <>
              {isLoading ? <Skeleton active paragraph={{ rows: 10 }} /> : renderResume()}
            </>
          )}

          {activeTab === "notes" && (
            <>
              {isLoading ? <Skeleton active paragraph={{ rows: 10 }} /> : renderNotes()}
            </>
          )}
        </div>
      </Content>
    </Layout>
  )
}


const resumeStyles = `
.monster-content div {
  position: static !important;
  margin-bottom: 8px;
}

.monster-content {
  font-family: 'Calibri, Arial, sans-serif';
  line-height: 1.5;
}

.monster-content b {
  font-weight: 600;
}

.ant-tabs-ink-bar {
  background-color: #1890ff !important;
  height: 3px !important;
}

.ant-tabs-tab.ant-tabs-tab-active .ant-tabs-tab-btn {
  color: #1890ff !important;
  font-weight: 500 !important;
}

.ant-tabs-tab:hover {
  color: #1890ff !important;
}
`;

export default CandidateDetailView

